import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Row, Col, Typography, Button, Tag, Spin, Descriptions, Card,
  Image, Space, Divider, message,
} from 'antd';
import {
  ArrowLeftOutlined, PhoneOutlined, DownloadOutlined,
  FileTextOutlined, StarFilled,
} from '@ant-design/icons';
import { scenarioSiteAPI } from '../../services/api';
import { getContentWrapperStyle } from './templateStyles';

const { Title, Text } = Typography;

export default function ScenarioSiteProduct({ slug, siteInfo }) {
  const { t, i18n } = useTranslation();
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await scenarioSiteAPI.getProduct(slug, productId, i18n.language);
        setProduct(res.data);
      } catch {
        message.error(t('publicSite.productDetail.notFound'));
        navigate(`/s/${slug}`);
      }
      setLoading(false);
    })();
  }, [slug, productId, navigate, i18n.language]);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;
  }
  if (!product) return null;

  const brandColor = siteInfo?.brand_color || '#1890ff';
  const template = siteInfo?.site_template || 'modern';
  const isMinimal = template === 'minimal';
  const isClassic = template === 'classic';

  const cardRadius = isMinimal ? 0 : isClassic ? 4 : 12;
  const btnRadius = isMinimal ? 0 : isClassic ? 4 : undefined;
  const imgRadius = isMinimal ? 0 : isClassic ? 4 : 8;
  const cardShadow = isMinimal ? 'none' : isClassic ? '0 1px 4px rgba(0,0,0,0.08)' : '0 2px 8px rgba(0,0,0,0.1)';

  const contentStyle = getContentWrapperStyle(template, brandColor);
  const images = product.images || [];
  const docs = product.documents || [];
  const specs = product.specifications || {};
  const features = product.features || [];

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px' }}>
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(`/s/${slug}/products`)}
        style={{ marginBottom: 16, paddingLeft: 0 }}
      >
        {t('publicSite.productDetail.backToList')}
      </Button>

      <Row gutter={[32, 24]}>
        <Col xs={24} md={10}>
          <div style={{
            background: '#f5f5f5', borderRadius: imgRadius, overflow: 'hidden',
            marginBottom: 16,
          }}>
            {product.thumbnail || images.length > 0 ? (
              <Image
                src={product.thumbnail || images[0]}
                alt={product.name}
                style={{ width: '100%', maxHeight: 400, objectFit: 'contain' }}
                fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg=="
              />
            ) : (
              <div style={{
                height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: `linear-gradient(135deg, ${brandColor}22, ${brandColor}44)`,
              }}>
                <FileTextOutlined style={{ fontSize: 64, color: brandColor }} />
              </div>
            )}
          </div>
          {images.length > 1 && (
            <Image.PreviewGroup>
              <Space wrap>
                {images.map((img, i) => (
                  <Image key={i} src={img} width={80} height={80} style={{ objectFit: 'cover', borderRadius: imgRadius }} />
                ))}
              </Space>
            </Image.PreviewGroup>
          )}
        </Col>

        <Col xs={24} md={14}>
          <div style={{ marginBottom: 8 }}>
            {product.organization && (
              <Tag color="blue" style={{ marginBottom: 8 }}>{product.organization}</Tag>
            )}
            {(product.tags || []).map((t, i) => (
              <Tag key={i} style={{ marginBottom: 8 }}>{t}</Tag>
            ))}
          </div>

          <Title level={2} style={{ marginTop: 0, marginBottom: 8 }}>{product.name}</Title>

          {product.short_description && (
            <Text type="secondary" style={{ fontSize: 15, display: 'block', marginBottom: 16 }}>
              {product.short_description}
            </Text>
          )}

          {product.custom_price_note && (
            <div style={{
              background: `${brandColor}11`, border: `1px solid ${brandColor}33`,
              borderRadius: isMinimal ? 0 : 8, padding: '12px 16px', marginBottom: 16,
            }}>
              <Text strong style={{ color: brandColor, fontSize: 16 }}>
                {product.custom_price_note}
              </Text>
            </div>
          )}

          <Space size={12} style={{ marginBottom: 24 }}>
            <Button
              type="primary"
              size="large"
              icon={<PhoneOutlined />}
              onClick={() => navigate(`/s/${slug}/contact?product=${product.id}&name=${encodeURIComponent(product.name)}`)}
              style={{
                background: brandColor,
                borderColor: brandColor,
                borderRadius: btnRadius,
              }}
            >
              {t('publicSite.productDetail.inquire')}
            </Button>
            <Button
              size="large"
              onClick={() => navigate(`/s/${slug}/loi`)}
              style={{ borderRadius: btnRadius }}
            >
              {t('publicSite.productDetail.fillLOI')}
            </Button>
          </Space>

          {docs.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>{t('publicSite.productDetail.relatedDocs')}</Text>
              <Space direction="vertical" size={4}>
                {docs.map((doc, i) => {
                  const name = typeof doc === 'string' ? doc.split('/').pop() : doc.name || t('placeholders.document', { num: i + 1 });
                  const url = typeof doc === 'string' ? doc : doc.url;
                  return (
                    <Button
                      key={i}
                      type="link"
                      icon={<DownloadOutlined />}
                      href={url}
                      target="_blank"
                      style={{ paddingLeft: 0 }}
                    >
                      {name}
                    </Button>
                  );
                })}
              </Space>
            </div>
          )}
        </Col>
      </Row>

      <Divider />

      {(product.custom_description || product.description) && (
        <div style={{ marginBottom: 32 }}>
          <Title level={4} style={{
            fontWeight: isMinimal ? 300 : undefined,
            letterSpacing: isMinimal ? 1 : 0,
            borderBottom: isMinimal ? `2px solid ${brandColor}` : 'none',
            paddingBottom: isMinimal ? 6 : 0,
            display: isMinimal ? 'inline-block' : 'block',
          }}>
            {t('publicSite.productDetail.description')}
          </Title>
          <div
            dangerouslySetInnerHTML={{
              __html: product.custom_description || product.description,
            }}
            style={contentStyle}
          />
        </div>
      )}

      {features.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <Title level={4} style={{
            fontWeight: isMinimal ? 300 : undefined,
            letterSpacing: isMinimal ? 1 : 0,
            borderBottom: isMinimal ? `2px solid ${brandColor}` : 'none',
            paddingBottom: isMinimal ? 6 : 0,
            display: isMinimal ? 'inline-block' : 'block',
          }}>
            {t('publicSite.productDetail.features')}
          </Title>
          <Row gutter={[16, 16]}>
            {features.map((f, i) => (
              <Col key={i} xs={24} sm={12} md={8}>
                <Card size="small" style={{
                  height: '100%',
                  borderRadius: cardRadius,
                  border: isMinimal ? '1px solid #e8e8e8' : undefined,
                  boxShadow: cardShadow,
                }}>
                  <Space align="start">
                    <StarFilled style={{ color: '#faad14', marginTop: 4 }} />
                    <Text>{typeof f === 'string' ? f : f.text || f.name || JSON.stringify(f)}</Text>
                  </Space>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      )}

      {Object.keys(specs).length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <Title level={4} style={{
            fontWeight: isMinimal ? 300 : undefined,
            letterSpacing: isMinimal ? 1 : 0,
            borderBottom: isMinimal ? `2px solid ${brandColor}` : 'none',
            paddingBottom: isMinimal ? 6 : 0,
            display: isMinimal ? 'inline-block' : 'block',
          }}>
            {t('publicSite.productDetail.specifications')}
          </Title>
          <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small">
            {Object.entries(specs).map(([key, val]) => (
              <Descriptions.Item key={key} label={key}>
                {typeof val === 'object' ? JSON.stringify(val) : String(val)}
              </Descriptions.Item>
            ))}
          </Descriptions>
        </div>
      )}
    </div>
  );
}
